/**
 * How a paused Python program asks the learner a question.
 *
 * The problem it solves: Pyodide's stdin callback **must return synchronously**, and
 * Pyodide runs in a Web Worker so the answer has to come from the main thread. A
 * worker cannot synchronously ask the main thread anything — unless it blocks on a
 * `SharedArrayBuffer` with `Atomics.wait`, which needs the page to be
 * cross-origin-isolated.
 *
 * That was ruled out in M2 (decisions.md D2/D3) because `COEP: require-corp` breaks
 * Strudel loading samples from GitHub. `COEP: credentialless` did not exist in usable
 * form then; it does now, and it was measured: isolation on, SharedArrayBuffer
 * available in page and worker, and zero failed cross-origin requests with Strudel's
 * sample manifest still returning 200.
 *
 * The handshake, from the worker's point of view:
 *
 *   1. write the prompt into the buffer, set state = ASKING, notify the main thread
 *   2. `Atomics.wait(state, ASKING)` — the worker thread stops here, doing nothing
 *   3. the main thread shows the prompt, takes an answer, writes it, sets state =
 *      ANSWERED or CANCELLED, and notifies
 *   4. the worker wakes, reads the answer, returns it to Pyodide, and Python carries on
 *
 * Why not just message-pass: postMessage is asynchronous, and there is nowhere to
 * await it — the stdin callback has to have a string in its hand when it returns.
 */

/** Slot 0 of the control array: whose turn it is. */
export const STATE = 0;
/** Slot 1: how many bytes of the data region are in use. */
export const LENGTH = 1;

export const IDLE = 0;
export const ASKING = 1;
export const ANSWERED = 2;
/** Stop was pressed, or the session ended: the read should behave like EOF. */
export const CANCELLED = 3;

/** Room for a prompt or an answer. A learner typing more than this into one answer is
 *  not a case worth carrying complexity for; it is truncated rather than lost. */
export const DATA_BYTES = 8 * 1024;
const CONTROL_SLOTS = 2;

export interface InputChannel {
  buffer: SharedArrayBuffer;
  control: Int32Array;
  data: Uint8Array;
}

export function createInputChannel(): InputChannel {
  const buffer = new SharedArrayBuffer(CONTROL_SLOTS * 4 + DATA_BYTES);
  return {
    buffer,
    control: new Int32Array(buffer, 0, CONTROL_SLOTS),
    data: new Uint8Array(buffer, CONTROL_SLOTS * 4, DATA_BYTES),
  };
}

/** Attach to a buffer received over postMessage. */
export function attachInputChannel(buffer: SharedArrayBuffer): InputChannel {
  return {
    buffer,
    control: new Int32Array(buffer, 0, CONTROL_SLOTS),
    data: new Uint8Array(buffer, CONTROL_SLOTS * 4, DATA_BYTES),
  };
}

export function writeString(channel: InputChannel, text: string): void {
  const bytes = new TextEncoder().encode(text);
  const length = Math.min(bytes.length, DATA_BYTES);
  channel.data.set(bytes.subarray(0, length));
  Atomics.store(channel.control, LENGTH, length);
}

export function readString(channel: InputChannel): string {
  const length = Atomics.load(channel.control, LENGTH);
  // The copy is required, not defensive. A browser's TextDecoder refuses a view onto a
  // SharedArrayBuffer outright:
  //
  //   TypeError: Failed to execute 'decode' on 'TextDecoder':
  //   The provided ArrayBufferView value must not be shared.
  //
  // Node's TextDecoder accepts one, so every unit test here passed and the failure
  // only appeared in a real browser — as an OSError inside Python, several layers
  // away from the cause. `new Uint8Array(view)` copies into an unshared buffer.
  const bytes = new Uint8Array(channel.data.subarray(0, length));
  return new TextDecoder("utf-8").decode(bytes);
}

/**
 * Is the environment able to do this at all?
 *
 * False on a page that is not cross-origin-isolated — plain HTTP without the headers,
 * or a browser that does not support `credentialless`. The runner falls back to
 * answers supplied before the run, so the feature degrades rather than breaking.
 */
export function interactiveInputAvailable(): boolean {
  return (
    typeof SharedArrayBuffer !== "undefined" &&
    typeof Atomics !== "undefined" &&
    typeof Atomics.wait === "function" &&
    // `crossOriginIsolated` is what actually gates SharedArrayBuffer; checking the
    // constructor alone is not enough, because it exists but is unusable without it.
    (globalThis as { crossOriginIsolated?: boolean }).crossOriginIsolated === true
  );
}

/**
 * Block this thread until the main thread answers. **Worker side only.**
 *
 * Returns null when cancelled, which the caller turns into EOF so `input()` raises
 * rather than silently handing back an empty string.
 */
export function askAndWait(
  channel: InputChannel,
  prompt: string,
  notify: (prompt: string) => void,
): string | null {
  writeString(channel, prompt);
  Atomics.store(channel.control, STATE, ASKING);
  notify(prompt);
  // Atomics.notify is for other threads waiting on this slot; the main thread learns
  // about the question from `notify` above, because it must not block.
  Atomics.notify(channel.control, STATE);

  // Waits indefinitely. Nothing here needs a timeout: the main thread pauses the
  // runaway-loop clock while a human is thinking, and Stop terminates the worker
  // outright, which ends this wait whether it is blocked or not.
  while (Atomics.load(channel.control, STATE) === ASKING) {
    Atomics.wait(channel.control, STATE, ASKING);
  }

  const state = Atomics.load(channel.control, STATE);
  const answer = state === ANSWERED ? readString(channel) : null;
  Atomics.store(channel.control, STATE, IDLE);
  return answer;
}

/** Hand an answer to the waiting worker. **Main thread side only.** */
export function answer(channel: InputChannel, text: string): void {
  writeString(channel, text);
  Atomics.store(channel.control, STATE, ANSWERED);
  Atomics.notify(channel.control, STATE);
}

/** Wake the worker with no answer, so `input()` sees EOF. **Main thread side only.** */
export function cancel(channel: InputChannel): void {
  Atomics.store(channel.control, LENGTH, 0);
  Atomics.store(channel.control, STATE, CANCELLED);
  Atomics.notify(channel.control, STATE);
}
